/** Nearby UI visible on frontend when both admin toggles are on. */
export function isNearLocationFeatureEnabled(config = {}) {
  return config?.enabled !== false && config?.show_on_home !== false;
}

/** @deprecated Same as isNearLocationFeatureEnabled — home section uses the combined gate. */
export function isNearLocationOnHome(config = {}) {
  return isNearLocationFeatureEnabled(config);
}

export const NEAR_LOCATION_DEFAULTS = {
  enabled: true,
  show_on_home: true,
  nearby_distance_km: 10,
  default_search_radius_km: 10,
  location_refresh_interval_sec: 60,
  config_refresh_interval_sec: 45,
  fallback_behavior: 'district',
  fallback_expand_radius_km: 25,
  results_per_page: 12,
  home_results_limit: 8,
  allow_manual_location: true,
  page_title: 'Nearby Locations',
  page_subtitle: 'Discover properties near your selected location',
  empty_message: 'No properties found within the configured distance.',
};

export function positiveKm(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return n;
}

/** Effective nearby search radius from admin config (default capped by max distance). */
export function resolveSearchRadiusKm(config = {}) {
  const cap = positiveKm(config.nearby_distance_km, NEAR_LOCATION_DEFAULTS.nearby_distance_km);
  const preferred = positiveKm(
    config.default_search_radius_km,
    NEAR_LOCATION_DEFAULTS.default_search_radius_km,
  );
  return Math.min(preferred, cap);
}

export function applyNearLocationConfig(data, heroFallback = {}) {
  const merged = { ...NEAR_LOCATION_DEFAULTS, ...(data && typeof data === 'object' ? data : {}) };
  const fromHero = heroFallback && typeof heroFallback === 'object' ? heroFallback : {};

  const cap = positiveKm(
    merged.nearby_distance_km ?? fromHero.nearby_radius_km,
    NEAR_LOCATION_DEFAULTS.nearby_distance_km,
  );
  const defaultRadius = positiveKm(
    merged.default_search_radius_km ?? merged.nearby_distance_km ?? fromHero.nearby_radius_km,
    NEAR_LOCATION_DEFAULTS.default_search_radius_km,
  );

  return {
    enabled: merged.enabled !== false && fromHero.nearby_properties_enabled !== false,
    show_on_home: merged.show_on_home !== false,
    nearby_distance_km: cap,
    default_search_radius_km: Math.min(defaultRadius, cap),
    location_refresh_interval_sec: Math.min(
      3600,
      Math.max(15, Math.round(Number(merged.location_refresh_interval_sec)) || NEAR_LOCATION_DEFAULTS.location_refresh_interval_sec),
    ),
    config_refresh_interval_sec: Math.min(
      600,
      Math.max(15, Math.round(Number(merged.config_refresh_interval_sec)) || NEAR_LOCATION_DEFAULTS.config_refresh_interval_sec),
    ),
    fallback_behavior: ['district', 'hide', 'expand_radius', 'message'].includes(merged.fallback_behavior)
      ? merged.fallback_behavior
      : NEAR_LOCATION_DEFAULTS.fallback_behavior,
    fallback_expand_radius_km: positiveKm(
      merged.fallback_expand_radius_km,
      NEAR_LOCATION_DEFAULTS.fallback_expand_radius_km,
    ),
    results_per_page: Math.min(50, Math.max(4, Math.round(Number(merged.results_per_page)) || NEAR_LOCATION_DEFAULTS.results_per_page)),
    home_results_limit: Math.min(24, Math.max(4, Math.round(Number(merged.home_results_limit)) || NEAR_LOCATION_DEFAULTS.home_results_limit)),
    allow_manual_location: merged.allow_manual_location !== false,
    page_title: String(merged.page_title || NEAR_LOCATION_DEFAULTS.page_title).trim(),
    page_subtitle: String(merged.page_subtitle || NEAR_LOCATION_DEFAULTS.page_subtitle).trim(),
    empty_message: String(merged.empty_message || NEAR_LOCATION_DEFAULTS.empty_message).trim(),
  };
}
